import { Component, h, State } from '@stencil/core';
import { doc, updateDoc } from '@firebase/firestore';

import { FirebaseService } from '../../utils/firebase';
import { settings } from '../../store/settings.store';
import { customer } from '../../store/customer.store';
import { customerFields, onCustomerFieldsChange, startCustomerFieldsListener, stopCustomerFieldsListener } from '../../store/customer-fields.store';

import { BusinessCustomerData, PlatformCustomerData } from '@artask/tambr-models/models/customer.model';
import { addCustomFieldToForm, getCustomFieldValues, getFormErrors, handleFormFieldChange, renderCustomerFields, renderField } from '../../utils/form';


@Component({
  tag: 'tambr-account-info',
  styleUrl: 'tambr-account-info.css',
  shadow: true,
})
export class TambrAccountInfo {

  @State() loading: boolean = false;
  @State() successMessages: string[] = [];
  @State() errorMessages: string[] = [];

  changePasswordDialogEl: HTMLTambrAuthPasswordUpdateModalElement;

  form = {
    email: { type: 'email', title: 'Email', value: '', isRequired: true }, 
    firstName: { type: 'text', title: 'First Name', value: '', isRequired: true },
    lastName: { type: 'text', title: 'Last Name', value: '', isRequired: true },
    phone: { type: 'text', title: 'Phone', value: '', isRequired: true },
    subscribed: { type: 'checkbox', title: 'Subscribed', value: false, isRequired: false }
  }

  componentWillLoad() {
    if ( customer.platformCustomer ) {
      this.form.email.value = customer.platformCustomer.email;
      this.form.firstName.value = customer.platformCustomer.firstName;
      this.form.lastName.value = customer.platformCustomer.lastName;
      this.form.phone.value = customer.platformCustomer.phone;
    }

    if ( customer.businessCustomer ) {
      this.form.subscribed.value = customer.businessCustomer?.subscribed;
    }

    startCustomerFieldsListener();

    onCustomerFieldsChange( 'customFields', customFields => {
      for ( let customField of customFields ) {
        const customFieldValue = customer?.businessCustomer?.customFields?.find( cf => cf.id === customField.id );
        addCustomFieldToForm( this.form, customField, customFieldValue );
      }
    });
  }

  disconnectedCallback() {
    stopCustomerFieldsListener();
  }

  changePasswordHandler(){
    if ( document.getElementsByTagName('tambr-auth-password-update-modal').length ) return;    
    this.changePasswordDialogEl = document.createElement('tambr-auth-password-update-modal');
    this.changePasswordDialogEl.addEventListener( 'tambrChangePasswordSuccess', () => { this.handleChangePasswordSuccess() });
    this.changePasswordDialogEl.addEventListener( 'tambrChangePasswordClose', () => { this.closeChangePasswordDialog() });
    document.body.appendChild( this.changePasswordDialogEl );
  }

  handleChangePasswordSuccess() {
    this.successMessages = [ ...this.successMessages, 'Your password has been updated.' ];
    this.closeChangePasswordDialog();
  }

  closeChangePasswordDialog() {
    if ( document.getElementsByTagName('tambr-auth-password-update-modal').length ) {
      document.body.removeChild( this.changePasswordDialogEl );
    }
  }


  async handleSubmit( ev: any ) {
    ev.preventDefault();
    this.successMessages = [];
    this.errorMessages = getFormErrors( this.form );
    if ( this.errorMessages.length ) return;
    this.loading = true;

    try {
      const platformCustomerData: Partial<PlatformCustomerData> = {
        firstName: this.form.firstName.value,
        lastName: this.form.lastName.value,
        phone: this.form.phone.value,
      };

      const platformCustomerDocRef = doc( FirebaseService.firestore, `users/${ customer.platformCustomer.uid }` );
      await updateDoc( platformCustomerDocRef, platformCustomerData );

      if ( settings.businessId && customer.businessCustomer ) {
        const businessCustomerData: Partial<BusinessCustomerData>  = {
          firstName: this.form.firstName.value,
          lastName: this.form.lastName.value,
          phone: this.form.phone.value,
          subscribed: this.form.subscribed.value,         
        };

        const customerFieldsValues = getCustomFieldValues( this.form, customerFields.customFields );

        if ( customerFieldsValues.length ) {
          businessCustomerData.customFields = customerFieldsValues;
        }

        const businessCustomerDocPath = `businesses/${ settings.businessId }/customers/${ customer.businessCustomer?.id }`;
        const businessCustomerDocRef = doc( FirebaseService.firestore, businessCustomerDocPath );
        await updateDoc( businessCustomerDocRef, businessCustomerData );
      }

      this.successMessages = [ ...this.successMessages, 'Your information has been updated' ];
       
    } catch ( error ) {
      this.errorMessages = [ ...this.errorMessages, error.message ]; 
    }
    this.loading = false;
  }


  render() {
    if ( customerFields.customerFieldsLoading ) {
      return this.renderPreloader();
    } else {
      return (
        <form onSubmit={ (ev) => this.handleSubmit(ev) }>
  
          { this.renderMessages() }
  
          <div class='tambr-form-grid'>
            { renderField( this.form, { id: 'firstName', title: 'First Name', type: 'text', isRequired: true } ) }
            { renderField( this.form, { id: 'lastName', title: 'Last Name', type: 'text', isRequired: true } ) }
            { renderField( this.form, { id: 'email', title: 'Email', type: 'text', isRequired: true }, true ) }
            { renderField( this.form, { id: 'phone', title: 'Phone', type: 'text', isRequired: true } ) }
          </div>
          
          <div class='tambr-form-item tambr-password-form-item'>
            <input type='password' id='password' class='has-value' name='password' readOnly disabled value='********'/>
            <label htmlFor='password'>Password</label>
            <tambr-icon class='edit-pass-btn' type='edit' onClick={ () => this.changePasswordHandler() } />
          </div>
  
          { renderCustomerFields( this.form ) }
  
          <div class='checkbox-wrapper'>
            <input type='checkbox' id='subscribe-checkbox' name='subscribe-checkbox' checked={ this.form.subscribed.value } 
              onInput={ (event) => handleFormFieldChange( this.form, event, 'subscribed', true ) } />
            <label htmlFor='subscribe-checkbox'>{`I would like to join ${ settings.businessSettings.businessName }'s mailing list.`}</label>
          </div>
  
          <div class='tambr-action-buttons'>
            <button type='submit' disabled={ this.loading } class={ 'tambr-button' + (this.loading ? ' loading' : '') }>
              <div class='tambr-button-loading'><div>Loading...</div></div>
              <span>Save</span>
            </button>
          </div>
        </form>
      );
    }
  }

  renderMessages() {
    return (
      <div class='sticky-messages'>
        <div class={'error message' + (this.errorMessages.length ? ' show' : '') }>
          <ul>{ this.errorMessages.map( (message: string) => <li>{ message }</li> ) }</ul>
        </div>

        <div class={'success message' + (this.successMessages.length ? ' show' : '') }>
          <ul>{ this.successMessages.map( (message: string) => <li>{ message }</li> ) }</ul>
        </div>
      </div>
    );
  }

  renderPreloader() {
    return (
      <div class='skeleton-card'>
        <div class='skeleton-grid'>
          <span class='skeleton-field' />
          <span class='skeleton-field' />
          <span class='skeleton-field' />
          <span class='skeleton-field' />
          <span class='skeleton-field' />
        </div>
        <div class="skeleton-text"></div>
        <div class='card-actions'>
          <div class='skeleton-button'/>
        </div>
      </div>
    )
  }

}
